import { Schema as S } from '@effect/schema'
import { Effect, RequestResolver } from 'effect'
import { UnknownException } from 'effect/Cause'
import { DownloadEntryId, ListNotFoundError } from 'features/ListBuilder/domain/Errors'
import { MarketingListId } from 'features/ListBuilder/domain/MarketingList'
import MarketingListRequest from 'features/ListBuilder/infra/repo/MarketingListRepoLive/MarketingListRequest'
import { DownloadEntrySchema } from 'features/ListBuilder/infra/repo/schema/DownloadEntrySchema'
import { HTTPError } from 'ky'
import { restClient } from 'presentation/libs/client'

/**
 * @NOTE: This endpoint creates a download entry that produces an id to be used for
 * downloading the "file" using another endpoint. The reason why it needs to create a download
 * entry is because it expects a range--using offset and limit--to specify partial download.
 * An explicit cursor offset prevents the backend from implicitly resuming an old download.
 */
const createDownloadEntry = (params: {
  listId: MarketingListId
  cursorOffset: number
}) =>
  Effect.tryPromise({
    try: async () => await restClient
      .post(`lists/v3/${params.listId}/downloads`, {
        json: {
          cursor: {
            offset: params.cursorOffset,
          },
        },
      })
      .json(),
    catch: error => {
      if (error instanceof HTTPError && error.response.status === 404)
        return new ListNotFoundError({ marketingListId: params.listId })

      return new UnknownException(error)
    },
  }).pipe(
    Effect.map(raw => S.decodeUnknownSync(DownloadEntrySchema)(raw)),
    Effect.map(decoded => toDomain(decoded)),
    Effect.catchTag('UnknownException', v => Effect.die(v)),
  )

const GetDownloadEntryResolver = RequestResolver.fromEffect(
  (request: MarketingListRequest.GetDownloadEntry) => createDownloadEntry({
    listId: request.listId,
    cursorOffset: request.cursorOffset,
  }),
)

export default GetDownloadEntryResolver

const toDomain = (schema: DownloadEntrySchema): DownloadEntryId => schema.id as DownloadEntryId
